import { useTranslation } from 'react-i18next';
import {
    VStack,
    HStack,
    Text,
    Button,
    Box,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from '../manage/ManageDialogFrame';
import { useAnalysisStore } from '../../state/useAnalysisStore';
import { analyticalModelManager, structuralLoadManager, feaSolver, rebuildAnalyticalModel } from '../../core/services';
import { bimKernel } from '../../core';
import {
    buildEnergyModelFromElements,
    calculateLoads,
    EnergyLoadResult,
} from '../../tools/analyze/energyEngine';

/**
 * Analysis Results dialog (BIM "Results and Color Fill" / check outputs):
 * shows the text report written by Analyze tools, structural run summary
 * with an inline re-solve, and the heating/cooling loads table.
 */
export function AnalysisResultsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((s) => s.manageDialog === 'analyzeResults');
    const onClose = useAppStore((s) => s.closeManageDialog);
    const report = useAnalysisStore((s) => s.report);
    const run = useAnalysisStore((s) => s.lastStructuralRun);
    const loads = useAnalysisStore((s) => s.lastLoads);
    const setReport = useAnalysisStore((s) => s.setReport);
    const setLoads = useAnalysisStore((s) => s.setLastLoads);
    const setRun = useAnalysisStore((s) => s.setLastStructuralRun);
    const setFeaResults = useAnalysisStore((s) => s.setLastFeaResults);
    const setShowResults = useAnalysisStore((s) => s.setShowResults);

    const rerunStructural = () => {
        // Rebuild + re-solve the active combination so results always match
        // the current model state (same pipeline as the Run tool).
        rebuildAnalyticalModel();
        let combination = structuralLoadManager.getAllCombinations()[0];
        if (!combination) {
            structuralLoadManager.generateStandardCombinations('LRFD');
            combination = structuralLoadManager.getAllCombinations()[0];
        }
        if (!combination) return;
        const results = feaSolver.analyze(combination.id);
        if (!results.isConverged) return;
        setFeaResults(results);
        setRun({
            combinationName: combination.name,
            maxDisplacement: results.maxDisplacement,
            maxStress: results.maxStress,
            converged: true,
            message: `Re-solved ${analyticalModelManager.getMembers().length} member(s).`,
        });
        setShowResults(true);
    };

    const recalcLoads = () => {
        const elements = Array.from(bimKernel.getProject().elements.values());
        const model = buildEnergyModelFromElements(elements);
        if (model.spaces.length === 0) return;
        const result = calculateLoads(model, useAnalysisStore.getState().energySettings);
        setLoads(result);
    };

    return (
        <ManageDialogFrame
            title={report ? report.title : 'Analysis Results'}
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            footer={
                <HStack justify="space-between" w="full">
                    <HStack>
                        <Button size="sm" variant="outline" onClick={rerunStructural}>{t('analyze.reRunStructural')}</Button>
                        <Button size="sm" variant="outline" onClick={recalcLoads}>{t('analyze.recalcLoads')}</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setReport(null); setLoads(null); }}>{t('analyze.clear')}</Button>
                    </HStack>
                    <ManageDialogCloseFooter onClose={onClose} />
                </HStack>
            }
        >
            <VStack spacing={3} align="stretch">
                {run && (
                    <Box borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-md)" p={3}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>{t('analyze.structuralAnalysis')}</Text>
                        <Text fontSize="xs">Combination: {run.combinationName}</Text>
                        <Text fontSize="xs">Max displacement: {(run.maxDisplacement * 1000).toFixed(2)} mm</Text>
                        <Text fontSize="xs">Max stress: {(run.maxStress / 1e6).toFixed(1)} MPa · {run.message}</Text>
                    </Box>
                )}
                {loads && (
                    <Box borderWidth="1px" borderColor="var(--color-border)" borderRadius="var(--cad-radius-md)" p={3}>
                        <Text fontSize="sm" fontWeight="bold" mb={1}>
                            Heating {`\u0026`} Cooling Loads — {(loads.totalHeatingW / 1000).toFixed(1)} kW / {(loads.totalCoolingW / 1000).toFixed(1)} kW
                        </Text>
                        <VStack align="stretch" spacing={0} maxH="140px" overflowY="auto">
                            {loads.perSpace.map((s: EnergyLoadResult['perSpace'][number]) => (
                                <HStack key={s.spaceName} justify="space-between">
                                    <Text fontSize="xs">{s.spaceName}</Text>
                                    <Text fontSize="xs" color="var(--color-text-secondary)">
                                        H {(s.heatingLoadW / 1000).toFixed(2)} kW · C {(s.coolingLoadW / 1000).toFixed(2)} kW
                                    </Text>
                                </HStack>
                            ))}
                        </VStack>
                    </Box>
                )}
                {report ? (
                    <Box
                        as="pre"
                        fontFamily="var(--font-mono, monospace)"
                        fontSize="xs"
                        whiteSpace="pre-wrap"
                        bg="var(--color-bg-tertiary)"
                        borderWidth="1px"
                        borderColor="var(--color-border)"
                        borderRadius="var(--cad-radius-md)"
                        p={3}
                        maxH="320px"
                        overflowY="auto"
                    >
                        {report.lines.join('\n')}
                    </Box>) : ( !run && !loads && (<Text fontSize="sm" color="var(--color-text-secondary)">{t('analyze.noAnalysisResultsYetRunAStructuralAnalysisASystemCheckOrAnEnergy')}</Text>
                    )
                )}
            </VStack>
        </ManageDialogFrame>
    );
}
